import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TableHeader } from "@/components/InmateDetails/styles";
import BookingQueueStepFilter from "./BookingQueueStepFilter";
import BookingQueueTable from "./BookingQueueTable";
import Button from "@/common/elements/Button";
import { PlusOutlined } from "@ant-design/icons";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import BookingQueueDetailsModal from "./BookingQueueDetailsModal";
import { setBookings } from "@/slices/bookingsSlice";
import { hasCheckSecurity } from "@/util/common_function";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";

const BookingQueueDetails = () => {
  const dispatch = useDispatch();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const { bookings, bookingsFieldsEnabled, manualBookingData } = useSelector(
    (state) => state.bookings
  );
  const [bookingQueueDetails, setBookingQueueDetails] = useState([]);

  const [queueDetailsModal, setQueueDetailsModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Update bookingQueueDetails whenever bookings changes
  useEffect(() => {
    setBookingQueueDetails(
      bookings?.bookingsQueue?.[0]?.bookingQueueDetails || []
    );
  }, [bookings]);

  useEffect(() => {
    if (manualBookingData?.isManualBooking) {
      handleOpenAddModal();
    }
  }, []);
  const handleStepChange = (step, checked) => {
    if (!checked) {
      // Remove any details with queStepID equal to the step value.
      const updatedDetails = bookingQueueDetails.filter(
        (detail) => detail.queStepID !== step
      );
      dispatch(
        setBookings({
          ...bookings,
          bookingsQueue: [{ bookingQueueDetails: updatedDetails }],
        })
      );
    }
  };

  const handleOpenAddModal = () => {
    setModalData(null);
    setIsEditing(false);
    setQueueDetailsModal(true);
  };

  const handleEditRow = (row) => {
    setQueueDetailsModal(true);
    setIsEditing(true);
  };

  const handleRowClick = (row) => {
    setModalData(row);
  };

  const handleSave = (data) => {
    const sanitizedData = {
      bookingQueueDetailID: data?.bookingQueueDetailID,
      queStepID: data?.queStepID,
      completionDate: data?.completionDate,
      completionTime: data?.completionTime,
      creatorStaffID: userDetails?.staffId,
      completionStaffID: data?.completionStaffID,
      comments: data?.comments,
      complete: data?.complete || false,
      finalStep: data?.finalStep || false,
    };

    // Remove bookingQueueDetailID if it contains "new"
    if (
      typeof sanitizedData.bookingQueueDetailID === "string" &&
      sanitizedData.bookingQueueDetailID.includes("new")
    ) {
      delete sanitizedData.bookingQueueDetailID;
    }

    if (isEditing) {
      // Replace the row entirely with the sanitized data
      const updatedDetails = bookingQueueDetails?.map((item) =>
        (item.bookingQueueDetailID &&
          item.bookingQueueDetailID === data.bookingQueueDetailID) ||
        item.queStepID === data.queStepID
          ? sanitizedData
          : item
      );

      dispatch(
        setBookings({
          ...bookings,
          bookingsQueue: [{ bookingQueueDetails: updatedDetails }],
        })
      );
    } else {
      // Add new row with sanitized data
      const newRow = { ...sanitizedData, id: bookingQueueDetails.length + 1 };
      dispatch(
        setBookings({
          ...bookings,
          bookingsQueue: [
            { bookingQueueDetails: [...bookingQueueDetails, newRow] },
          ],
        })
      );
    }
  };

  const handleCloseModal = () => {
    setQueueDetailsModal(false);
    setModalData(null);
    setIsEditing(false);
  };

  return (
    <>
      <BookingQueueStepFilter
        onStepChange={handleStepChange}
        onOpenModal={(step) => {
          setModalData(step);
          setQueueDetailsModal(true);
        }}
        bookingQueueDetails={bookingQueueDetails}
        disabled={!bookingsFieldsEnabled}
      />

      <TableHeader style={{ marginTop: "15px", flexWrap: "wrap" }}>
        <h6>Booking Queue Details</h6>
        <div>
          <StyledGridRow style={{ width: "100%" }}>
            <StyledGridCol>
              <Button
                style={{ marginRight: "10px" }}
                onClick={handleOpenAddModal}
                disabled={
                  !bookingsFieldsEnabled ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.ADD,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  )
                }
              >
                Add
              </Button>
            </StyledGridCol>
            {bookingQueueDetails.length > 0 && (
              <StyledGridCol>
                <Button
                  style={{ marginRight: "10px" }}
                  onClick={handleEditRow}
                  disabled={
                    !modalData ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.module,
                      LABELS_CAPITAL.VIEW,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    )
                  }
                >
                  Details
                </Button>
              </StyledGridCol>
            )}
          </StyledGridRow>
        </div>
      </TableHeader>
      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.module,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS
      ) && (
        <BookingQueueTable
          data={bookingQueueDetails}
          selected={!modalData}
          onRowClick={handleRowClick}
        />
      )}

      {queueDetailsModal && (
        <BookingQueueDetailsModal
          show={queueDetailsModal}
          onHide={handleCloseModal}
          stepData={modalData}
          onSave={handleSave}
          bookings={bookings}
          isEditing={isEditing}
        />
      )}
    </>
  );
};

export default BookingQueueDetails;
